import '../components/static-map-embed-modal';
import '../components/theme/form-button';

import { LitElement, css, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators';
import { isDefined, setMarkerDraggable } from '../helpers/utils';
import { pointRequestFromPoint, resendPointEmail } from '../helpers/api/points-api';

import { GetPointResponse } from '../types';
import { Menu } from '@material/mwc-menu';
import { StateController } from '../helpers/state/state-management/state-controller';
import { StaticMapEmbedModal } from './static-map-embed-modal';
import { getMainPage } from '../helpers/page-name';
import { logWithAmplitude } from '../helpers/api/api';
import pointCache from '../helpers/state/point-cache';
import { removeShapeFromMap } from '../helpers/shapes';
import topicData from '../helpers/state/topic-data';

@customElement('point-card-menu')
export class PointCardMenu extends LitElement {
    static amplitude_callsite: string = 'Point Card Menu';



    @query('#menu')
    menu!: Menu;

    @query('#menuTrigger')
    menuTrigger!: HTMLDivElement;

    @query('#embedModal')
    embedModal!: StaticMapEmbedModal;

    @property({ type: Object })
    point?: GetPointResponse;

    @state()
    get isFeatured() {
        return this.point?.featured === true;
    }

    @state()
    get isInactive() {
        return this.point?.active === false;
    }

    private readonly bindPointCacheState = new StateController(this, pointCache);
    private readonly bindTopicState = new StateController(this, topicData);

    async firstUpdated() {
        await this.startup();
    }

    private async startup() {
        this.menu.anchor = this.menuTrigger;
    }

    private openMenu() {
        this.menu.open = true;
    }

    protected render() {
        return html`
            <div class="menu__wrapper">
                <div id="menuTrigger" class="menu__trigger">
                    <mwc-icon-button raised @click=${this.openMenu} icon="more_horiz"></mwc-icon-button>
                </div>
                <mwc-menu id="menu" menuCorner="END" corner="BOTTOM_END" class="menu">
                    <span class="list-item" @click="${this.toggleFeatured}">
                        <mwc-icon class="menu__icon">star</mwc-icon>
                        ${this.isFeatured ? `Remove ${topicData.topic?.featured_name ?? 'Featured'} Flag` : topicData.topic?.featured_name ?? 'Feature'}
                    </span>
                    ${this.point?.email ? html`
                        <span class="list-item" @click="${this.onResendEmailClick}">
                            <mwc-icon class="menu__icon">send</mwc-icon>
                            Resend Point Add Email to ${this.point.email}
                        </span>
                    ` : ''}
                    ${this.point?.email ? html`
                        <span class="list-item" @click="${this.onUpdateOwnerEmailClick}">
                            <mwc-icon class="menu__icon">email</mwc-icon>
                            Update Owner Email
                        </span>
                    ` : html`
                        <span class="list-item" @click="${this.onUpdateOwnerEmailClick}">
                            <mwc-icon class="menu__icon">email</mwc-icon>
                            Set Point Creator Email
                        </span>
                    `}
                    <span class="list-item" @click="${this.onCopySecretEditLinkClick}">
                        <mwc-icon class="menu__icon">file_copy</mwc-icon>
                        Copy Secret Edit Link
                    </span>
                    <span class="list-item" @click="${this.onCopyStaticMapEmbedLinkClick}">
                        <mwc-icon class="menu__icon">share</mwc-icon>
                        Copy Static Map Embed Link
                    </span>
                    <span class="list-item" @click="${this.onDragClick}">
                        <mwc-icon class="menu__icon">drag_indicator</mwc-icon>
                        Drag
                    </span>
                    ${this.isInactive ?
                        ''
                        : html`<span class="list-item" @click="${this.onDeactivateClick}">
                                    <mwc-icon class="menu__icon">visibility_off</mwc-icon>
                                    Deactivate Point
                                </span>`
                    }
                    <span class="list-item" @click="${this.onDeleteClick}">
                        <mwc-icon class="menu__icon">delete</mwc-icon>
                        Delete
                    </span>
                </mwc-menu>
            </div>
            <static-map-embed-modal
                id="embedModal"
                .point="${this.point}"
                @show-toast="${(e: CustomEvent) => getMainPage().errorToastElement.showToast(e.detail.message)}"
            ></static-map-embed-modal>
            <error-toast id="errorToast"></error-toast>
        `;
    }

    private async toggleFeatured() {
        if (this.isFeatured) {
            await this.editFeaturePoint(false);
            logWithAmplitude(PointCardMenu.amplitude_callsite, 'Unfeature Click', {
                point_id: this.point?._id.$oid,
                topic_id: topicData.topicId
            });
        } else {
            await this.editFeaturePoint(true);

            logWithAmplitude(PointCardMenu.amplitude_callsite, 'Feature Click', {
                point_id: this.point?._id.$oid,
                topic_id: topicData.topicId
            });
       }

       topicData.mapComponent?.closeListOfPoints();
    }

    private async editFeaturePoint(featurePoint: boolean) {
        if (isDefined(this.point)) {
            this.point.featured = featurePoint;
            const pointRequest = pointRequestFromPoint(this.point);

            await getMainPage().errorToastElement.tryOrErrorToast(async () => {
                if (isDefined(this.point)) {
                    await pointCache.modifyAndFetch(this.point._id.$oid, pointRequest, true);
                }

            }, `An error has occurred.  Context: could not ${featurePoint ? 'Feature' : 'Unfeature'} the point.`);
        }
    }

    private async editPointEmail(email: string) {
        if (isDefined(this.point) && isDefined(this.point._id)) {
            this.point.email = email;
            const pointRequest = pointRequestFromPoint(this.point);

            await getMainPage().errorToastElement.tryOrErrorToast(async () => {
                if (isDefined(this.point)) {
                    await pointCache.modifyAndFetch(this.point._id.$oid, pointRequest, true);
                }

            }, 'An error has occurred.  Context: could not update the point email.');
        }
    }

    private onDragClick() {
        const marker = pointCache.getMarker(this.point?._id.$oid ?? '');
        if (isDefined(marker)) {
            setMarkerDraggable(marker.inner, true);
        }

        logWithAmplitude(PointCardMenu.amplitude_callsite, 'Drag Click', {
            point_id: this.point?._id.$oid,
            topic_id: topicData.topicId
        });
    }

    private async onDeactivateClick() {
        if (isDefined(this.point)) {
            this.point.active = false;
            const pointRequest = pointRequestFromPoint(this.point);

            await getMainPage().errorToastElement.tryOrErrorToast(async () => {
                if (isDefined(this.point)) {
                    await pointCache.modifyAndFetch(this.point._id.$oid, pointRequest, true);
                }

            }, 'An error has occurred.  Context: could not deactivate the point.');
        }

        topicData.mapComponent?.closeListOfPoints();

        logWithAmplitude(PointCardMenu.amplitude_callsite, 'Deactivate Click', {
            point_id: this.point?._id.$oid,
            topic_id: topicData.topicId
        });
    }

    private async onUpdateOwnerEmailClick() {
        // ask for new email
        let newEmail = prompt('Enter new email for point creator. They will receive an email with a link to edit this point only.');
        if (!newEmail) {
            return;
        }

        newEmail = newEmail.trim();

        if (newEmail === this.point?.email) {
            return;
        }

        await this.editPointEmail(newEmail);

        logWithAmplitude(PointCardMenu.amplitude_callsite, 'Update Owner Email Click', {
            point_id: this.point?._id.$oid,
            topic_id: topicData.topicId
        });

        // send email
        await this.onResendEmailClick();

        // show toast
        getMainPage().errorToastElement.showToast('Owner email updated and email sent.');

        // close menu
        this.menu.open = false;
    }

    private async onDeleteClick() {
        await getMainPage().errorToastElement?.tryOrErrorToast(async () => {
            if (isDefined(this.point)) {
                await pointCache.remove(this.point?._id.$oid);
            }

        }, 'An error has occurred.  Context: the server rejected the request due to validation issues.');

        topicData.mapComponent?.closeListOfPoints();

        if (isDefined(this.point) && isDefined(this.point.custom_shape)) {
            const holder = pointCache.getShapeHolderForPoint(this.point);
            if (isDefined(holder)) {
                removeShapeFromMap(holder.inner);
            }
        }

        logWithAmplitude(PointCardMenu.amplitude_callsite, 'Delete Click', {
            point_id: this.point?._id.$oid,
            topic_id: topicData.topicId
        });
    }

    private async onResendEmailClick() {
        if (isDefined(this.point) && isDefined(this.point._id)) {
            try {
                await resendPointEmail(PointCardMenu.amplitude_callsite, this.point._id?.$oid, topicData.topicKey);
                getMainPage().errorToastElement?.showToast('Email sent successfully.');
            } catch (e) {
                getMainPage().errorToastElement?.showToastAndThrow('An error has occurred.  Context: could not resend the email.');
            }
        }
    }

    private async onCopySecretEditLinkClick() {
        if (isDefined(this.point) && isDefined(this.point._id)) {
            const url = this.point.write_link ?? '';
            await navigator.clipboard.writeText(url);
            getMainPage().errorToastElement.showToast('Secret edit link copied to clipboard.');
        }
    }

    private async onCopyStaticMapEmbedLinkClick() {
        if (isDefined(this.point) && isDefined(this.point._id) && isDefined(topicData.topic)) {
            this.embedModal.open(true);

            logWithAmplitude(PointCardMenu.amplitude_callsite, 'Copy Static Map Embed Link Click', {
                point_id: this.point._id.$oid,
                topic_id: topicData.topicId
            });
        }
    }

    static get styles() {
        return css`
            .menu {
                --mdc-menu-z-index: 1;
                --mdc-typography-button-text-transform: case;
                --mdc-typography-button-letter-spacing: 0px;
            }

            .menu__icon {
                vertical-align: bottom;
                padding-right: 4px;
                color: var(--theme-lighter-text-color);
                --mdc-icon-size: 12px;
            }

            .list-item {
                display: flex;
                align-items: center;
                padding: 8px 16px;
                cursor: pointer;
                line-height: 1rem;
                font-size: 0.8rem;
                min-width:15vw;
            }

            .list-item:hover {
                background-color: var(--theme-border-color);
            }

            .menu__wrapper {
                position: relative;
            }

            .menu__trigger {
                display: flex;
                align-items: center;
                background-color: var(--main-featured-color);
                display: flex;
                margin-right: 4px;
                border-radius: 4px;
            }
        `;
    }
}
